import Box from 'UIKit/Box/Box'
import EmptyContent from 'UIKit/EmptyContent'
import { HEIGHT_ROW } from 'UIKit/Table/TableHistory'
import LoadingTable from 'UIKit/Table/TableHistory/LoadingTable'
import { useEffect, useRef, useState } from 'react'
import { BaseResponse, HunnyRequest, Paging } from 'services/types'

import Flex from 'UIKit/Box/Flex'
import Text from 'UIKit/Text'
import { BetDetails } from 'config/types/profile'
import useDebounce from 'hooks/useDebounce'
import { Trans } from 'react-i18next'
import styled from 'styled-components'
import { range } from 'utils'
import LiveBetRow from './LiveBetRow'
import { LIVE_BET_ANIMATION_DURATION } from './config'
import useDisplayLiveBet from './useDisplayLiveBet'

const MAX_LIMIT_LIST_LIVE_BET = 31
const HEADER_PADING_Y = 12

const LiveBetTable: React.FC<{
  fetchFn: () => HunnyRequest<BaseResponse<Paging<BetDetails>>>
  validateBet: (bet: BetDetails) => boolean
  displayRowCount: number
}> = ({ fetchFn, validateBet, displayRowCount }) => {
  const [isInview, setIsInview] = useState(false)
  const isInviewDebounced = useDebounce(isInview, 5000)

  const liveBet = useDisplayLiveBet(
    validateBet,
    {
      animationDuration: LIVE_BET_ANIMATION_DURATION,
      limit: MAX_LIMIT_LIST_LIVE_BET,
    },
    fetchFn,
  )

  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let observer: IntersectionObserver

    if (containerRef.current) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const { isIntersecting } = entry
            setIsInview(isIntersecting)
          })
        },
        {
          root: null,
          rootMargin: '100px',
          threshold: 0,
        },
      )
      observer.observe(containerRef.current)
    }

    return () => {
      if (observer) {
        observer.disconnect()
      }
    }
  }, [])

  useEffect(() => {
    if (!isInview && !isInviewDebounced) {
      liveBet.pause()
    } else {
      liveBet.play()
    }
  }, [isInview, isInviewDebounced])
  return (
    <Box
      position="relative"
      minHeight="200px"
      height={`${2 * HEADER_PADING_Y + HEIGHT_ROW + displayRowCount * HEIGHT_ROW}px`}
      ref={containerRef}
    >
      <StyledHeader>
        <HeaderItem width={['40%', '', '30%', '', '']}>
          <Text fontWeight={600} fontSize="14px" color="textSubtle" display="flex" textAlign="center" p="0px 8px">
            <Trans>Game</Trans>
          </Text>
        </HeaderItem>
        <HeaderItem width={['20%', '', '', '', '30%']}>
          <Text fontWeight={600} fontSize="14px" color="textSubtle" display="flex" textAlign="center" p="0px 8px">
            <Trans>Player</Trans>
          </Text>
        </HeaderItem>
        <HeaderItem width={['0%', '', '25%', '', '']} display={['none', '', 'flex']}>
          <Text fontWeight={600} fontSize="14px" color="textSubtle" display="flex" textAlign="center" p="0px 8px">
            <Trans>Bet Amount</Trans>
          </Text>
        </HeaderItem>
        <HeaderItem width={['0%', '', '', '', '15%']} display={['none', '', '', '', 'flex']}>
          <Text fontWeight={600} fontSize="14px" color="textSubtle" display="flex" textAlign="center" p="0px 8px">
            <Trans>Multiplier</Trans>
          </Text>
        </HeaderItem>
        <HeaderItem width={['40%', '', '25%', '', '20%']}>
          <Text fontWeight={600} fontSize="14px" color="textSubtle" display="flex" textAlign="center" p="0px 8px">
            <Trans> Payout</Trans>
          </Text>
        </HeaderItem>
      </StyledHeader>

      <StyledContainer>
        {liveBet.isFetching ? (
          <>
            <LoadingRow>
              <LoadingCell>
                <LoadingTable loading top="0" />
              </LoadingCell>
            </LoadingRow>

            {range(0, displayRowCount).map((idx) => (
              <LoadingRow key={`table-row-loader-${idx}`}>
                <LoadingCell>
                  <Box width="100%" height="52px" />
                </LoadingCell>
              </LoadingRow>
            ))}
          </>
        ) : (
          <>
            {liveBet.bets.slice(0, Math.min(displayRowCount + 1, MAX_LIMIT_LIST_LIVE_BET)).map((result) => {
              return (
                <LiveBetRow
                  key={`bet-row-${result.item.betId}`}
                  betDetails={result.item}
                  className={result.active ? 'active' : ''}
                  animationRef={result.animationRef}
                />
              )
            })}
          </>
        )}
      </StyledContainer>

      {!liveBet.bets?.length && !liveBet.isFetching && (
        <EmptyContent content="No live bet history to show" my={40} justifyContent="center" />
      )}
    </Box>
  )
}

const StyledHeader = styled(Box)`
  display: flex;
  flex-direction: row;
  width: 100%;
  padding: ${HEADER_PADING_Y}px 16px;
  background: ${({ theme }) => theme.colors.background};
  position: relative;
  z-index: 1;
`

const HeaderItem = styled(Flex)`
  height: ${HEIGHT_ROW}px;
  align-items: center;
  overflow: hidden;
`

const StyledContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  position: relative;
  z-index: 0;

  .active {
    background: rgba(255, 255, 255, 0.04);
  }
`

const LoadingRow = styled(Box)`
  width: 100%;
  display: flex;
`

const LoadingCell = styled(Box)`
  width: 100%;
  position: relative;
`

export default LiveBetTable
